import apiBase from '../utils/baseApi';

export interface MyCreationData {
  url: string;
  previewImage?: string;
  albumThumbnail?: string;
  genres?: string[];
  title: string;
  description?: string;
  lyrics?: string;
  lyricsTimestamped?: string;
  duration?: number;
}

export interface ContributorDetails {
  companionId?: string;
  slug?: string;
  fullName?: string;
  previewImage?: string;
  videoDesktopUrl?: string;
  videoMobileUrl?: string;
}

export interface SaveMyCreationRequest {
  requestId: string;
  assetType: 'Track' | 'Image';
  query: string;
  contributorId: string;
  contributorDetails?: ContributorDetails;
  data: MyCreationData;
  musicGenerationMetadata?: {
    taskId?: string;
    conversionId?: string;
    linkedTaskId?: string;
  };
}

export interface SaveMyCreationResponse {
  success: boolean;
  message: string;
  id?: string;
}

export interface Creator {
  id: string;
  fullName: string;
  email: string;
}

export interface Contributor {
  id: string;
  fullName: string;
  companionId?: string;
  slug: string;
  previewImage: string;
  previewVideo?: string;
  previewMobileVideo?: string;
  previewMobileImage?: string;
  previewVideoImage?: string;
  previewMobileVideoImage?: string;
}

export interface MyCreation {
  id: string;
  requestId: string;
  assetType: 'Track' | 'Image' | 'Artifact';
  query: string;
  status: 'draft' | 'published' | 'archived';
  creator: Creator;
  contributor: Contributor;
  data: Record<string, any>;
  createdAt: string;
  updatedAt?: string;
  contestEntered?: boolean;
}

export interface PaginationInfo {
  total: number;
  limit: number;
  offset: number;
  hasNext: boolean;
  hasPrevious: boolean;
  totalPages: number;
  currentPage: number;
}

export interface GetMyCreationsResponse {
  items: MyCreation[];
  pagination: PaginationInfo;
}

export interface GetMyCreationsParams {
  assetType?: 'Track' | 'Image' | 'Artifact';
  requestId?: string;
  limit?: number;
  offset?: number;
}

export const saveMyCreation = async (
  data: SaveMyCreationRequest,
): Promise<SaveMyCreationResponse> => {
  try {
    const response = await apiBase.post<SaveMyCreationResponse>(
      '/api/my-creations',
      data,
    );
    return response.data;
  } catch (error) {
    console.error('Error saving my creation:', error);
    throw error;
  }
};

export const getMyCreations = async (
  params?: GetMyCreationsParams,
): Promise<GetMyCreationsResponse> => {
  try {
    const response = await apiBase.get<GetMyCreationsResponse>(
      '/api/my-creations',
      { params },
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching my creations:', error);
    throw error;
  }
};

export const deleteMyCreation = async (id: string): Promise<void> => {
  try {
    await apiBase.delete(`/api/my-creations/${id}`);
  } catch (error) {
    console.error('Error deleting my creation:', error);
    throw error;
  }
};

export interface UpdateMyCreationRequest {
  data?: Partial<MyCreationData>;
  query?: string;
}

export const updateMyCreation = async (
  id: string,
  updateData: UpdateMyCreationRequest,
): Promise<SaveMyCreationResponse> => {
  try {
    const response = await apiBase.patch<SaveMyCreationResponse>(
      `/api/my-creations/${id}`,
      updateData,
    );
    return response.data;
  } catch (error) {
    console.error('Error updating my creation:', error);
    throw error;
  }
};

export interface EnterContestRequest {
  email: string;
  contestId?: string;
}

export const enterContest = async (
  id: string,
  data: EnterContestRequest,
): Promise<MyCreation> => {
  try {
    const response = await apiBase.post<MyCreation>(
      `/api/my-creations/${id}/contest-entry`,
      data,
    );
    return response.data;
  } catch (error) {
    console.error('Error entering contest:', error);
    throw error;
  }
};

export const getCreationTrack = async (
  orgId: string,
  trackId: string,
): Promise<MyCreation | { error: string; status: number }> => {
  try {
    const response = await apiBase.get<MyCreation>(
      `/api/creations/${orgId}/${trackId}`,
    );
    return response.data;
  } catch (error: any) {
    console.error('Error fetching creation track:', error);
    // Return error info to distinguish between 404 and other errors
    if (error?.response?.status === 404) {
      return { error: 'not_found', status: 404 };
    }
    throw error;
  }
};
